import {
  fetchEventSource,
  EventStreamContentType,
} from 'api/sse'
import type { EventSourceMessage } from 'api/sse'

/** Thrown from fetchEventSource callbacks to trigger automatic retry. */
class RetriableError extends Error {}

/** Thrown from fetchEventSource callbacks to permanently stop the connection. */
class FatalError extends Error {}

/** Max consecutive errors before giving up. Prevents infinite retry on persistent failures. */
const MAX_CONSECUTIVE_ERRORS = 5

interface SseHandlers {
  onEvent: (event: string, data: string, id: string) => void
  onOpen?: () => void
  onError?: (err: unknown) => void
  onFatalError?: (err: Error) => void
}

interface SseClientOptions {
  url: string
  accessToken?: string
  cursor?: string
  signal?: AbortSignal
  handlers: SseHandlers
}

/**
 * Opens an SSE connection to the given URL with bearer token auth and Last-Event-ID support.
 * Automatically retries on transient errors (up to MAX_CONSECUTIVE_ERRORS).
 * Surfaces fatal errors (403, auth, too many retries) to the caller.
 */
export function openSseConnection({
  url,
  accessToken,
  cursor,
  signal,
  handlers,
}: SseClientOptions): { close: () => void } {
  const abortController = new AbortController()
  let consecutiveErrors = 0

  if (signal) {
    signal.addEventListener('abort', () => abortController.abort(), { once: true })
  }

  const headers: Record<string, string> = {}
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`
  }

  // Track the latest cursor so retries resume from the right position.
  let lastSeenId = cursor ?? '0'

  function buildSseUrl(): string {
    const c = lastSeenId
    if (c && c !== '0') {
      return `${url}${url.includes('?') ? '&' : '?'}lastEventId=${encodeURIComponent(c)}`
    }
    return url
  }

  // TODO: The proper fix is to add `last-event-id` to the CORS allow-list in
  // @luxuryescapes/router (or Envoy). Until then we use a custom fetch wrapper
  // because fetchEventSource mutates the headers object in-place to add
  // `last-event-id` after receiving SSE events with an id field. On retry the
  // mutated header triggers a CORS preflight that the server rejects, breaking
  // every reconnect attempt. The wrapper strips the header and injects the
  // cursor into the URL query param instead.
  const customFetch: typeof window.fetch = (input, init) => {
    const h = new Headers(init?.headers)
    h.delete('last-event-id')
    return window.fetch(buildSseUrl(), { ...init, headers: h })
  }

  fetchEventSource(buildSseUrl(), {
    fetch: customFetch,
    headers,
    credentials: accessToken ? undefined : 'include',
    signal: abortController.signal,

    async onopen(response: Response) {
      if (response.ok && response.headers.get('content-type')?.startsWith(EventStreamContentType)) {
        consecutiveErrors = 0
        handlers.onOpen?.()
        return
      }

      if (response.ok) {
        throw new FatalError(`Expected SSE content-type, got: ${response.headers.get('content-type')}`)
      }

      if (response.status === 403) {
        throw new FatalError('Forbidden — please log in again')
      }

      if (response.status >= 400 && response.status < 500 && response.status !== 429) {
        throw new FatalError(`Client error: ${response.status}`)
      }

      throw new RetriableError(`Server error: ${response.status}`)
    },

    onmessage(msg: EventSourceMessage) {
      consecutiveErrors = 0
      if (msg.id) lastSeenId = msg.id
      handlers.onEvent(msg.event || 'message', msg.data, msg.id)
    },

    onclose() {
      throw new RetriableError('Server closed connection')
    },

    onerror(err: unknown) {
      if (err instanceof FatalError) {
        throw err
      }

      consecutiveErrors++
      if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
        throw new FatalError(`SSE failed after ${MAX_CONSECUTIVE_ERRORS} consecutive errors: ${err}`)
      }

      handlers.onError?.(err)
    },
  }).catch((err: unknown) => {
    if (err instanceof FatalError) {
      handlers.onFatalError?.(err)
    }
  })

  return {
    close: () => abortController.abort(),
  }
}
